import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCalendarAlt,
  FaCheck,
  FaSyncAlt,
  FaTimes,
  FaFlag,
  FaPause,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPlay,
  FaBrain,
} from 'react-icons/fa';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechSelect,
  TechInput,
  TechTextarea,
  TechButton,
  TechButtonGhost,
  TechButtonDanger,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  listObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  seedObjectivesFromAI,
} from '../../services/platformService';

/* ---------- estilos locales ---------- */

const ObjectiveCard = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid
    ${({ $status }) =>
      $status === 'done' ? 'var(--alert-success)' :
      $status === 'at_risk' ? 'var(--alert-error)' :
      $status === 'paused' ? 'var(--alert-warning)' :
      accent};
  border-radius: 6px;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const CardHeader = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  flex: 1;
  min-width: 220px;
  color: var(--text-primary);
  font-size: 1rem;
  letter-spacing: -0.005em;
  word-break: break-word;
`;

const PriorityChip = styled.span`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 2px 6px;
  border: 1px solid;
  border-radius: 3px;
  flex-shrink: 0;
  ${({ $level }) => {
    if ($level === 'high')   return `color: var(--alert-error);   border-color: rgba(239, 71, 111, 0.4); background: rgba(239, 71, 111, 0.08);`;
    if ($level === 'low')    return `color: var(--text-disabled); border-color: rgba(255, 255, 255, 0.12); background: transparent;`;
    return `color: ${accent}; border-color: rgba(0, 180, 216, 0.35); background: rgba(0, 180, 216, 0.08);`;
  }}
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid;
  flex-shrink: 0;
  ${({ $status }) => {
    if ($status === 'done')    return `color: var(--alert-success); border-color: rgba(6, 214, 160, 0.4); background: rgba(6, 214, 160, 0.08);`;
    if ($status === 'at_risk') return `color: var(--alert-error);   border-color: rgba(239, 71, 111, 0.4); background: rgba(239, 71, 111, 0.08);`;
    if ($status === 'paused')  return `color: var(--alert-warning); border-color: rgba(255, 191, 0, 0.4); background: rgba(255, 191, 0, 0.08);`;
    return `color: ${accent}; border-color: rgba(0, 180, 216, 0.35); background: rgba(0, 180, 216, 0.08);`;
  }}

  svg { font-size: 0.7rem; }
`;

const Description = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.55;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);

  svg { color: ${accent}; font-size: 0.75rem; }
`;

const ProgressTrack = styled.div`
  position: relative;
  height: 6px;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
  background: ${({ $status }) =>
    $status === 'done' ? 'var(--alert-success)' :
    $status === 'at_risk' ? 'var(--alert-error)' :
    accent};
`;

const ProgressLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-bottom: 0.3rem;

  strong { color: var(--text-primary); font-weight: 600; }
`;

const KrList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const KrItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.4rem 0.6rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 3px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover { border-color: rgba(0, 180, 216, 0.35); }

  .text {
    flex: 1;
    font-size: 0.85rem;
    color: ${({ $done }) => ($done ? 'var(--text-disabled)' : 'var(--text-secondary)')};
    text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
    word-break: break-word;
  }
`;

const KrCheckbox = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid ${({ $done }) => ($done ? 'var(--alert-success)' : 'rgba(255,255,255,0.2)')};
  background: ${({ $done }) => ($done ? 'rgba(6, 214, 160, 0.18)' : 'transparent')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg { color: var(--alert-success); font-size: 9px; }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.4rem;
  margin-top: 0.25rem;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4rem 1rem 2rem;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 540px;
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1.25rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const ModalTitle = styled.h3`
  margin: 0 0 1rem;
  font-family: ${mono};
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.55rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${accent};
    box-shadow: 0 0 0 3px rgba(0, 180, 216, 0.18);
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-bottom: 0.85rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.85rem;

  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const KrEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const KrEditorRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 140px auto;
  gap: 0.4rem;
  align-items: center;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
    border: 1px solid ${borderSoft};
    border-radius: 4px;
    padding: 0.5rem;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
`;

/* ---------- helpers ---------- */

const TIMEFRAMES = [
  { id: 'short',  label: 'Corto plazo',  hint: '1-3 meses' },
  { id: 'medium', label: 'Mediano plazo', hint: '3-12 meses' },
  { id: 'long',   label: 'Largo plazo',  hint: '+1 año' },
];

const STATUS_OPTIONS = [
  { value: 'active',  label: 'En curso',  icon: <FaPlay /> },
  { value: 'at_risk', label: 'En riesgo', icon: <FaExclamationTriangle /> },
  { value: 'paused',  label: 'Pausado',   icon: <FaPause /> },
  { value: 'done',    label: 'Cumplido',  icon: <FaCheckCircle /> },
];

const PRIORITY_OPTIONS = [
  { value: 'high',   label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low',    label: 'Baja' },
];

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function deadlineState(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d)) return null;
  const diff = d - new Date();
  const days = Math.round(diff / (24 * 3600 * 1000));
  if (days < 0) return { kind: 'overdue', days: Math.abs(days), label: `vencido hace ${Math.abs(days)}d` };
  if (days <= 14) return { kind: 'soon', days, label: `vence en ${days}d` };
  return { kind: 'ok', days, label: fmtDate(deadline) };
}

function statusIcon(status) {
  const found = STATUS_OPTIONS.find((s) => s.value === status);
  return found?.icon || null;
}

function statusLabel(status) {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
}

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'short',
  priority: 'medium',
  status: 'active',
  progress: 0,
  deadline: '',
  owner: '',
  key_results: [],
};

/* ---------- componente principal ---------- */

const Planning = () => {
  const [activeTab, setActiveTab] = useState('short');
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedReport, setSeedReport] = useState(null);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    listObjectives()
      .then((items) => setState({ loading: false, error: null, items: items || [] }))
      .catch((err) => setState({ loading: false, error: friendlyError(err, 'No se pudieron cargar los objetivos.'), items: [] }));
  };

  useEffect(load, []);

  const grouped = useMemo(() => {
    const buckets = { short: [], medium: [], long: [], other: [] };
    for (const o of state.items) {
      const c = (o.category || '').toLowerCase();
      if (c === 'short' || c === 'medium' || c === 'long') buckets[c].push(o);
      else buckets.other.push(o);
    }
    return buckets;
  }, [state.items]);

  const totals = useMemo(() => {
    const t = { active: 0, done: 0, at_risk: 0, paused: 0 };
    for (const o of state.items) {
      if (t[o.status] !== undefined) t[o.status] += 1;
    }
    return t;
  }, [state.items]);

  const tabs = TIMEFRAMES.map((t) => ({
    id: t.id,
    label: `${t.label} · ${grouped[t.id]?.length || 0}`,
  }));

  const openNew = () => {
    setForm({ ...EMPTY_FORM, category: activeTab });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (obj) => {
    setForm({
      title: obj.title || '',
      description: obj.description || '',
      category: obj.category || 'short',
      priority: obj.priority || 'medium',
      status: obj.status || 'active',
      progress: Number(obj.progress) || 0,
      deadline: obj.deadline ? String(obj.deadline).slice(0, 10) : '',
      owner: obj.owner || '',
      key_results: Array.isArray(obj.key_results) ? obj.key_results : [],
    });
    setEditingId(obj.id);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateKr = (idx, patch) => {
    setForm((f) => ({
      ...f,
      key_results: f.key_results.map((kr, i) => (i === idx ? { ...kr, ...patch } : kr)),
    }));
  };
  const addKr = () =>
    setForm((f) => ({
      ...f,
      key_results: [...f.key_results, { text: '', done: false, due: '' }],
    }));
  const removeKr = (idx) =>
    setForm((f) => ({ ...f, key_results: f.key_results.filter((_, i) => i !== idx) }));

  const submit = async () => {
    const title = form.title.trim();
    if (!title) {
      alert('El título es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title,
        description: form.description.trim() || null,
        category: form.category,
        priority: form.priority,
        status: form.status,
        progress: Math.max(0, Math.min(100, Number(form.progress) || 0)),
        deadline: form.deadline || null,
        owner: form.owner.trim() || null,
        key_results: form.key_results
          .filter((kr) => (kr.text || '').trim())
          .map((kr) => ({
            text: kr.text.trim(),
            done: !!kr.done,
            due: kr.due || null,
          })),
      };
      if (editingId) {
        await updateObjective(editingId, payload);
      } else {
        await createObjective(payload);
      }
      closeModal();
      load();
    } catch (err) {
      alert('No se pudo guardar: ' + friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSeedFromAI = async ({ clear = false } = {}) => {
    const message = clear
      ? '¿Reemplazar TODOS los objetivos actuales por un nuevo set generado por IA?\nEsta acción borra los existentes y no se puede deshacer.'
      : '¿Generar el primer set de objetivos con IA?\nLa IA leerá toda la plataforma (competidores, noticias, hallazgos, redes, electoral) y propondrá ~9 objetivos distribuidos en corto/mediano/largo plazo.\n\nPuede tardar 30-60 segundos.';
    if (!window.confirm(message)) return;

    setSeeding(true);
    setSeedReport(null);
    try {
      const data = await seedObjectivesFromAI({ clear });
      setSeedReport({
        ok: true,
        inserted: data.inserted_count,
        proposed: data.proposed_count,
        cleared: data.cleared,
        summary: data.context_summary,
      });
      load();
    } catch (err) {
      setSeedReport({ ok: false, error: friendlyError(err, 'No se pudieron generar los objetivos.') });
    } finally {
      setSeeding(false);
    }
  };

  const remove = async (obj) => {
    if (!window.confirm(`¿Eliminar "${obj.title}"?`)) return;
    try {
      await deleteObjective(obj.id);
      load();
    } catch (err) {
      alert('No se pudo eliminar: ' + friendlyError(err));
    }
  };

  const toggleKr = async (obj, idx) => {
    const krs = Array.isArray(obj.key_results) ? obj.key_results : [];
    const updated = krs.map((kr, i) => (i === idx ? { ...kr, done: !kr.done } : kr));
    const completed = updated.filter((kr) => kr.done).length;
    const newProgress = updated.length > 0 ? Math.round((completed / updated.length) * 100) : obj.progress;
    try {
      await updateObjective(obj.id, { key_results: updated, progress: newProgress });
      load();
    } catch (err) {
      alert('No se pudo actualizar: ' + friendlyError(err));
    }
  };

  const renderCard = (obj) => {
    const krs = Array.isArray(obj.key_results) ? obj.key_results : [];
    const dl = deadlineState(obj.deadline);
    return (
      <ObjectiveCard key={obj.id} $status={obj.status}>
        <CardHeader>
          <CardTitle>{obj.title}</CardTitle>
          <StatusChip $status={obj.status}>
            {statusIcon(obj.status)}
            {statusLabel(obj.status)}
          </StatusChip>
          <PriorityChip $level={obj.priority}>{obj.priority || 'medium'}</PriorityChip>
        </CardHeader>

        {obj.description && <Description>{obj.description}</Description>}

        <MetaRow>
          <span><FaCalendarAlt /> {fmtDate(obj.deadline)}</span>
          {dl && dl.kind !== 'ok' && (
            <span style={{ color: dl.kind === 'overdue' ? 'var(--alert-error)' : 'var(--alert-warning)' }}>
              · {dl.label}
            </span>
          )}
          {obj.owner && <span>· <FaFlag /> {obj.owner}</span>}
        </MetaRow>

        <div>
          <ProgressLine>
            <span>Progreso</span>
            <strong>{Number(obj.progress) || 0}%</strong>
          </ProgressLine>
          <ProgressTrack>
            <ProgressFill $pct={Number(obj.progress) || 0} $status={obj.status} />
          </ProgressTrack>
        </div>

        {krs.length > 0 && (
          <KrList>
            {krs.map((kr, i) => (
              <KrItem
                key={i}
                $done={kr.done}
                onClick={() => toggleKr(obj, i)}
                title="Click para marcar/desmarcar"
              >
                <KrCheckbox $done={kr.done}>{kr.done && <FaCheck />}</KrCheckbox>
                <span className="text">{kr.text}</span>
                {kr.due && (
                  <span style={{
                    fontFamily: mono,
                    fontSize: '0.62rem',
                    letterSpacing: '0.12em',
                    color: 'var(--text-disabled)',
                  }}>
                    {fmtDate(kr.due)}
                  </span>
                )}
              </KrItem>
            ))}
          </KrList>
        )}

        <Actions>
          <TechButtonGhost type="button" onClick={() => openEdit(obj)}>
            <FaEdit /> Editar
          </TechButtonGhost>
          <TechButtonDanger type="button" onClick={() => remove(obj)}>
            <FaTrash /> Eliminar
          </TechButtonDanger>
        </Actions>
      </ObjectiveCard>
    );
  };

  const tabItems = grouped[activeTab] || [];
  const tabHint = TIMEFRAMES.find((t) => t.id === activeTab)?.hint;

  return (
    <TechShell>
      <TechPageHeader
        title="Planificación de Objetivos"
        subtitle="La hoja de ruta del mandato 2025–2029: tus objetivos ordenados por plazo, guardados y disponibles para todo el equipo."
        dossier="OPS://PLAN-OBJ/01"
        actions={
          <>
            <TechButtonGhost type="button" onClick={load} disabled={state.loading || seeding}>
              <FaSyncAlt /> Refrescar
            </TechButtonGhost>
            <TechButtonGhost
              type="button"
              onClick={() => handleSeedFromAI({ clear: state.items.length > 0 })}
              disabled={seeding}
              title="Lee toda la plataforma y propone ~9 objetivos"
            >
              <FaBrain /> {seeding ? 'Generando…' : state.items.length > 0 ? 'Regenerar con IA' : 'Generar con IA'}
            </TechButtonGhost>
            <TechButton type="button" onClick={openNew} disabled={seeding}>
              <FaPlus /> Nuevo objetivo
            </TechButton>
          </>
        }
      >
        <StatusPill>Total <strong>{state.items.length}</strong></StatusPill>
        <StatusPill>En curso <strong>{totals.active}</strong></StatusPill>
        <StatusPill>En riesgo <strong>{totals.at_risk}</strong></StatusPill>
        <StatusPill>Cumplidos <strong>{totals.done}</strong></StatusPill>
      </TechPageHeader>

      {seeding && (
        <div style={{
          marginBottom: '1.25rem',
          padding: '0.85rem 1.1rem',
          border: '1px solid rgba(0, 180, 216, 0.4)',
          borderRadius: 6,
          background: 'rgba(0, 180, 216, 0.06)',
          fontFamily: mono,
          fontSize: '0.78rem',
          color: 'var(--text-primary)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.7rem',
        }}>
          <FaBrain style={{ color: accent }} />
          IA leyendo plataforma + generando objetivos · esto suele tardar entre 20 y 60 segundos…
        </div>
      )}

      {seedReport && (
        <div style={{
          marginBottom: '1.25rem',
          padding: '0.85rem 1.1rem',
          border: `1px solid ${seedReport.ok ? 'rgba(6, 214, 160, 0.4)' : 'rgba(239, 71, 111, 0.4)'}`,
          borderRadius: 6,
          background: seedReport.ok ? 'rgba(6, 214, 160, 0.06)' : 'rgba(239, 71, 111, 0.06)',
          fontFamily: mono,
          fontSize: '0.74rem',
          letterSpacing: '0.04em',
          color: 'var(--text-secondary)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '0.7rem',
        }}>
          <div style={{ flex: 1 }}>
            {seedReport.ok ? (
              <>
                <strong style={{ color: 'var(--alert-success)' }}>
                  ✓ {seedReport.inserted} objetivos insertados
                </strong>
                {' '}({seedReport.proposed} propuestos, {seedReport.cleared ? 'lista limpiada antes' : 'agregados al set existente'})
                {seedReport.summary && (
                  <div style={{ marginTop: 4, color: 'var(--text-disabled)' }}>
                    Contexto: {seedReport.summary.competidores} competidores · {seedReport.summary.noticias_scrapeadas} noticias · {seedReport.summary.insights_ia} insights · {seedReport.summary.redes_propias} redes
                  </div>
                )}
              </>
            ) : (
              <>
                <strong style={{ color: 'var(--alert-error)' }}>✗ Falló:</strong>{' '}
                {seedReport.error}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => setSeedReport(null)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-disabled)',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      )}

      <StatStrip min="160px" style={{ marginBottom: '1.25rem' }}>
        {TIMEFRAMES.map((t) => (
          <StatTile key={t.id}>
            <StatLabel><FaCalendarAlt /> {t.label}</StatLabel>
            <StatValue>{grouped[t.id]?.length || 0}</StatValue>
            <StatSub>{t.hint}</StatSub>
          </StatTile>
        ))}
        <StatTile>
          <StatLabel><FaCheckCircle /> Cumplidos</StatLabel>
          <StatValue style={{ color: 'var(--alert-success)' }}>{totals.done}</StatValue>
          <StatSub>de {state.items.length} totales</StatSub>
        </StatTile>
      </StatStrip>

      <TechTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <TechSection title={`${tabItems.length} objetivos · ${tabHint}`}>
        {state.loading && <Loading>Cargando objetivos</Loading>}
        {state.error && <ErrorBox>No se pudo cargar · {state.error}</ErrorBox>}
        {!state.loading && !state.error && tabItems.length === 0 && (
          <Empty>
            Sin objetivos en este plazo. Usá <strong>“Nuevo objetivo”</strong> arriba para crear el primero.
            Quedan guardados y disponibles para todo el equipo.
          </Empty>
        )}
        {!state.loading && !state.error && tabItems.length > 0 && (
          <Cards>{tabItems.map(renderCard)}</Cards>
        )}
      </TechSection>

      {modalOpen && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <ModalCard>
            <ModalTitle>{editingId ? 'Editar objetivo' : 'Nuevo objetivo'}</ModalTitle>

            <FieldLabel>
              Título
              <TechInput
                value={form.title}
                onChange={(e) => handleField('title', e.target.value)}
                placeholder="Ej: Presentar Plan de Tasas Municipales en sesión ordinaria"
              />
            </FieldLabel>

            <FieldLabel>
              Descripción
              <TechTextarea
                value={form.description}
                onChange={(e) => handleField('description', e.target.value)}
                placeholder="Contexto, alcance, audiencia objetivo…"
                rows={3}
              />
            </FieldLabel>

            <FieldRow>
              <FieldLabel>
                Plazo
                <TechSelect value={form.category} onChange={(e) => handleField('category', e.target.value)}>
                  {TIMEFRAMES.map((t) => (
                    <option key={t.id} value={t.id}>{t.label} · {t.hint}</option>
                  ))}
                </TechSelect>
              </FieldLabel>
              <FieldLabel>
                Prioridad
                <TechSelect value={form.priority} onChange={(e) => handleField('priority', e.target.value)}>
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </TechSelect>
              </FieldLabel>
            </FieldRow>

            <FieldRow>
              <FieldLabel>
                Estado
                <TechSelect value={form.status} onChange={(e) => handleField('status', e.target.value)}>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </TechSelect>
              </FieldLabel>
              <FieldLabel>
                Progreso (%)
                <TechInput
                  type="number"
                  min={0}
                  max={100}
                  value={form.progress}
                  onChange={(e) => handleField('progress', e.target.value)}
                />
              </FieldLabel>
            </FieldRow>

            <FieldRow>
              <FieldLabel>
                Fecha límite
                <TechInput
                  type="date"
                  value={form.deadline}
                  onChange={(e) => handleField('deadline', e.target.value)}
                />
              </FieldLabel>
              <FieldLabel>
                Responsable
                <TechInput
                  value={form.owner}
                  onChange={(e) => handleField('owner', e.target.value)}
                  placeholder="Ej: Equipo de prensa"
                />
              </FieldLabel>
            </FieldRow>

            <FieldLabel as="div">
              Resultados clave (key results)
              <KrEditor>
                {form.key_results.length === 0 && (
                  <span style={{ color: 'var(--text-disabled)', textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>
                    Sin resultados clave. Agregá uno para descomponer el objetivo.
                  </span>
                )}
                {form.key_results.map((kr, idx) => (
                  <KrEditorRow key={idx}>
                    <TechInput
                      value={kr.text}
                      onChange={(e) => updateKr(idx, { text: e.target.value })}
                      placeholder={`KR ${idx + 1} · entregable medible`}
                    />
                    <TechInput
                      type="date"
                      value={kr.due || ''}
                      onChange={(e) => updateKr(idx, { due: e.target.value })}
                    />
                    <TechButtonGhost type="button" onClick={() => removeKr(idx)}>
                      <FaTimes />
                    </TechButtonGhost>
                  </KrEditorRow>
                ))}
                <TechButtonGhost type="button" onClick={addKr} style={{ alignSelf: 'flex-start' }}>
                  <FaPlus /> Agregar key result
                </TechButtonGhost>
              </KrEditor>
            </FieldLabel>

            <Actions style={{ justifyContent: 'flex-end', marginTop: '0.5rem' }}>
              <TechButtonGhost type="button" onClick={closeModal} disabled={saving}>
                Cancelar
              </TechButtonGhost>
              <TechButton type="button" onClick={submit} disabled={saving}>
                {saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Crear objetivo'}
              </TechButton>
            </Actions>
          </ModalCard>
        </Modal>
      )}

      <p style={{
        marginTop: '1rem',
        fontFamily: mono,
        fontSize: '0.62rem',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--text-disabled)',
        textAlign: 'center',
      }}>
        Los objetivos se guardan en la plataforma y los comparte todo el equipo
      </p>
    </TechShell>
  );
};

export default Planning;
